import { EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS } from "@repo/caip";
import { z } from "zod";

const canonicalAccount = z.string().regex(/^eip155:[1-9]\d*:0x[0-9a-f]{40}$/u);
const canonicalAsset = z
  .string()
  .regex(/^eip155:[1-9]\d*\/(?:erc20:0x[0-9a-f]{40}|slip44:60)$/u)
  .refine(
    (value) =>
      !value.endsWith(`/erc20:${EVM_NATIVE_TOKEN_PLACEHOLDER_ADDRESS}`),
  );
const positiveUint256 = z
  .string()
  .regex(/^[1-9]\d*$/u)
  .max(78)
  .refine((value) => {
    try {
      return BigInt(value) < 1n << 256n;
    } catch {
      return false;
    }
  });

/** Persisted server-owned selection, executed as one atomic UserOperation. */
export const OwnerWithdrawalBatchArgsSchema = z
  .object({
    executionAccountRef: canonicalAccount,
    recipient: z
      .string()
      .regex(/^0x[0-9a-fA-F]{40}$/u)
      .refine((value) => value !== "0x0000000000000000000000000000000000000000")
      .transform((value) => value.toLowerCase()),
    assets: z
      .array(
        z
          .object({
            assetId: canonicalAsset,
            amountRaw: positiveUint256,
          })
          .strict(),
      )
      .min(1)
      .max(50),
  })
  .strict()
  .superRefine((args, ctx) => {
    const chainRef = args.executionAccountRef.slice(
      0,
      args.executionAccountRef.lastIndexOf(":"),
    );
    const chainId = Number(chainRef.slice("eip155:".length));
    if (!Number.isSafeInteger(chainId) || chainId <= 0) {
      ctx.addIssue({
        code: "custom",
        path: ["executionAccountRef"],
        message: "Execution account must use a safe canonical EVM chain",
      });
    }
    let previous: string | undefined;
    for (const [index, asset] of args.assets.entries()) {
      if (!asset.assetId.startsWith(`${chainRef}/`)) {
        ctx.addIssue({
          code: "custom",
          path: ["assets", index, "assetId"],
          message: "Every asset must use the execution account chain",
        });
      }
      if (previous !== undefined && previous >= asset.assetId) {
        ctx.addIssue({
          code: "custom",
          path: ["assets", index, "assetId"],
          message: "Assets must be sorted and unique",
        });
      }
      previous = asset.assetId;
    }
  });

export type OwnerWithdrawalBatchArgs = z.infer<
  typeof OwnerWithdrawalBatchArgsSchema
>;
